import cn from 'classnames'
import { FC } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { Button } from 'core-components/library/button'
import { Tag } from 'partner360/components/tag/Tag'
import { SIGNER_PICKER_TRANSLATION_KEYS } from 'partner360/features/acknowledgmentSignerPicker/translations'
import { getTranslatedText } from 'partner360/helpers/getTranslatedText'

import { isCurrentUserCommitment } from '../lib/buildAcknowledgmentStatusView'
import { formatDisplayDate } from '../lib/formatDisplayDate'
import { getOptionalToggle } from '../lib/getOptionalToggle'
import { isReminderSentToday } from '../lib/isReminderSentToday'
import { CommitmentStatusKeys, CommitmentTypes } from '../model/enums'
import type { AcknowledgmentCommitmentItem, AcknowledgmentCommitmentSummary } from '../model/types'
import { ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS } from '../translations'

import { AcknowledgmentCopyEmailButton } from './AcknowledgmentCopyEmailButton'
import { AcknowledgmentSecondaryActionsMenu } from './AcknowledgmentSecondaryActionsMenu'

import styles from './AcknowledgmentTypeColumn.module.scss'

const FIELD_KEYS = ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.table

type AcknowledgmentTypeColumnProps = {
  summary: AcknowledgmentCommitmentSummary
  items: AcknowledgmentCommitmentItem[]
  currentUserEmail: string
  canManageSigners: boolean
  /** A SuperAdmin's second add button in the PBM column: the Manager form, saved as a PBM. */
  canAddPbmByEmail: boolean
  isSubmitting: boolean
  onSign: (item: AcknowledgmentCommitmentItem) => void
  onRequestNotify: (item: AcknowledgmentCommitmentItem) => void
  onAddPartner: (commitmentType: CommitmentTypes) => void
  onAddPbmByEmail: (typeName: string) => void
  onRequestRefuse: (item: AcknowledgmentCommitmentItem) => void
  onRequestDrop: (item: AcknowledgmentCommitmentItem) => void
  onRequestChangeSigner: (item: AcknowledgmentCommitmentItem) => void
  onRequestRemove: (item: AcknowledgmentCommitmentItem) => void
  onSetOptional: (item: AcknowledgmentCommitmentItem, isOptional: boolean) => void
  onOpenPlanChanges: (item: AcknowledgmentCommitmentItem) => void
  onConfirmManager: (item: AcknowledgmentCommitmentItem) => void
  onSimulate?: (item: AcknowledgmentCommitmentItem) => void
  /** Lets a link scroll to the column, e.g. `ack-section/hp`. */
  anchorId?: string
}

export const AcknowledgmentTypeColumn: FC<AcknowledgmentTypeColumnProps> = ({
  summary,
  items,
  currentUserEmail,
  canManageSigners,
  canAddPbmByEmail,
  isSubmitting,
  onSign,
  onRequestNotify,
  onAddPartner,
  onAddPbmByEmail,
  onRequestRefuse,
  onRequestDrop,
  onRequestChangeSigner,
  onRequestRemove,
  onSetOptional,
  onOpenPlanChanges,
  onConfirmManager,
  onSimulate,
  anchorId,
}) => {
  const t = useSafetyTranslation()
  /* The column is done once every mandatory signer has signed; an optional signer still waiting
     does not hold it open. */
  const mandatoryItems = items.filter(
    (item) => !(Boolean(item.IsOptional) && Boolean(item.SignerEmail?.trim()))
  )
  const isDone =
    mandatoryItems.length > 0
      ? mandatoryItems.every((item) => item.IsAcknowledged === 1)
      : summary.signedCount === summary.totalCount
  const typeName =
    summary.typeName ||
    getTranslatedText({
      t,
      key: ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.other,
    })

  return (
    <div id={anchorId} className={styles.column}>
      <div className={styles.columnHeader}>
        <span
          className={cn(styles.columnIndicator, {
            [styles.columnIndicatorDone]: isDone,
          })}
        />
        <span className={styles.columnTitle}>
          <Translation
            span
            text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.acknowledgment}
            replaceParams={[{ key: '{typeName}', value: typeName }]}
          />
        </span>
        <span className={styles.columnMeta}>
          {summary.signedCount}/{summary.totalCount}
        </span>
        {/* Stands left of the usual button, so that one keeps its place for everyone. */}
        {canAddPbmByEmail && summary.commitmentType === CommitmentTypes.PBM && (
          <Button
            title={getTranslatedText({
              t,
              key: ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.addPbmByEmail,
              replaceParams: [{ key: '{typeName}', value: typeName }],
            })}
            variant="text"
            color="warning"
            disabled={isSubmitting}
            onClick={() => onAddPbmByEmail(typeName)}
            iconButton="hpicon hpicon-add-contact"
          />
        )}
        {canManageSigners && (
          <Button
            // className={styles.addButton}
            title={getTranslatedText({
              t,
              key: ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.addPartnerToSigningList,
            })}
            variant="text"
            disabled={isSubmitting}
            onClick={() => onAddPartner(summary.commitmentType)}
            iconButton="hpicon hpicon-add-contact"
          />
        )}
      </div>

      <div className={styles.columnList}>
        {items.length === 0 ? (
          <div className={styles.emptyState}>
            <Translation text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.noItems} />
          </div>
        ) : (
          [...items]
            .sort((a, b) => (a.IsOptional ?? 0) - (b.IsOptional ?? 0))
            .map((item, index) => {
              const isCurrentUser = isCurrentUserCommitment(item, currentUserEmail)
              const canOpenPlanChanges = item.CommitmentStatusKey === CommitmentStatusKeys.Dropped
              const comment = item.Comment?.trim() ?? ''
              const email = item.SignerEmail?.trim() ?? ''
              const role = item.SignerRole?.trim() ?? ''
              const isOptional = Boolean(item.IsOptional) && Boolean(email)
              /* `-1` is an act the server holds until another plan is complete: the button stays,
                 switched off, as the partner page drew it; the reason stands above the columns. */
              const canResend = item.CanResend ?? 0
              const showsSign = item.CanSignOff > 0 || (item.CanSignOff < 0 && canResend <= 0)

              return (
                <div
                  key={`${item.CommitmentTypeName}-${item.EntryId}-${index}`}
                  className={cn(styles.item, {
                    [styles.itemCurrentUser]: isCurrentUser,
                    [styles.itemOptional]: isOptional,
                  })}
                >
                  <div className={styles.itemMain}>
                    <div className={styles.itemTopRow}>
                      <div className={styles.itemName}>
                        <span>{item.SignerName?.trim() || '—'}</span>
                        {email && (
                          <Tag
                            variant="info"
                            shape="pill"
                            appearance={isOptional ? 'dashed' : 'soft'}
                            marker
                            translation={
                              <>
                                {isOptional ? (
                                  <Translation
                                    text={
                                      ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.optional
                                    }
                                    span
                                  />
                                ) : (
                                  <Translation text={SIGNER_PICKER_TRANSLATION_KEYS.mandatory} />
                                )}
                              </>
                            }
                          />
                        )}
                        {isCurrentUser && (
                          <span className={styles.youBadge}>
                            <Translation
                              text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.you}
                              span
                            />
                          </span>
                        )}
                      </div>
                      <AcknowledgmentSecondaryActionsMenu
                        item={item}
                        isSubmitting={isSubmitting}
                        onRequestRefuse={onRequestRefuse}
                        onRequestDrop={onRequestDrop}
                        onRequestChangeSigner={onRequestChangeSigner}
                        onRequestRemove={onRequestRemove}
                        optionalToggle={canManageSigners ? getOptionalToggle(item, items) : null}
                        onSetOptional={onSetOptional}
                        onConfirmManager={onConfirmManager}
                        onSimulate={onSimulate}
                      />
                    </div>
                    {/* Every field is drawn, with a dash for a missing value, so the same line
                    stands at the same height in every card. */}
                    <dl className={styles.itemFields}>
                      <dt className={styles.itemFieldLabel}>
                        <Translation text={FIELD_KEYS.role} span />
                      </dt>
                      <dd className={styles.itemRole}>{role || '—'}</dd>
                      <dt className={styles.itemFieldLabel}>
                        <Translation text={FIELD_KEYS.email} span />
                      </dt>
                      <dd className={styles.itemEmail}>
                        <span className={styles.itemEmailText}>{email || '—'}</span>
                        {email && <AcknowledgmentCopyEmailButton email={email} />}
                      </dd>
                    </dl>
                    {comment ? (
                      <div className={styles.itemComment}>
                        <span className={styles.itemCommentLabel}>
                          <Translation text="partnercommitment-comment" span />
                        </span>
                        <span className={styles.itemCommentValue}>{comment}</span>
                      </div>
                    ) : null}
                  </div>

                  <div className={styles.itemBottom}>
                    <div className={styles.itemStatus}>
                      <span
                        className={styles.statusValue}
                        style={{ color: item.CommitmentStatusColor || '#475467' }}
                      >
                        {item.CommitmentStatusIcon && (
                          <i className={cn(item.CommitmentStatusIcon, styles.statusIcon)} />
                        )}
                        <span>{item.CommitmentStatus || '—'}</span>
                        {/* The date belongs to the status it was set with, so it is said with it. */}
                        {item.ActionDate && <span>· {formatDisplayDate(item.ActionDate)}</span>}
                      </span>
                      {canOpenPlanChanges ? (
                        <Button
                          variant="text"
                          className={styles.detailsButton}
                          onClick={() => onOpenPlanChanges(item)}
                        >
                          <Translation text="jbp-commitment-changes-details" span />
                        </Button>
                      ) : null}
                    </div>

                    {showsSign ? (
                      <div className={styles.itemActions}>
                        <Button
                          className={styles.actionButton}
                          color="success"
                          size="sm"
                          disabled={isSubmitting || item.CanSignOff < 0}
                          onClick={() => onSign(item)}
                        >
                          <Translation
                            text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.sign}
                            span
                          />
                        </Button>
                      </div>
                    ) : canResend !== 0 ? (
                      <div className={styles.itemActions}>
                        {item.EmailSentDate ? (
                          <span className={styles.itemNotifyDate}>
                            {formatDisplayDate(item.EmailSentDate, 'dateTime')}
                          </span>
                        ) : null}
                        <Button
                          className={cn(styles.actionButton, styles.actionButtonNotify)}
                          size="sm"
                          color="warning"
                          disabled={
                            isSubmitting || canResend < 0 || isReminderSentToday(item.EmailSentDate)
                          }
                          onClick={() => onRequestNotify(item)}
                        >
                          <Translation
                            text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.notify}
                            span
                          />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              )
            })
        )}
      </div>
    </div>
  )
}
